import { useTranslation } from "react-i18next";
import { useRef, useEffect, useContext, useState } from "react";
import { AppContext } from "../context/AppContext";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import Pack from "./Pack";

import styles from "../css/heatmaps/PriceList.module.css";

import ButtonBtn from "./buttons/ButtonBtn";
import formatCurrency from "../utils/functions/formatCurrency";
import axiosInstance from "../api/AxiosConfig";
import ModalLoading from "./ModalLoading";

// Načtení správné base URL z .env souboru podle prostředí (produkce/vývoj)
const BASE_URL = process.env.REACT_APP_API_URL_PROD;

// Query funkce: přepíná mezi axios (auth) a fetch (public)
const fetchServicePlans = async ({ queryKey, signal }) => {
  const [, isLogged] = queryKey;

  if (isLogged) {
    // Přihlášený uživatel: použij axiosInstance (auth headers, cookies, interceptory)
    const { data } = await axiosInstance.get(`service-plans/heatmapy/`, {
      signal,
    });
    return data;
  }

  // Nepřihlášený: veřejný endpoint přes fetch
  const res = await fetch(`${BASE_URL}service-plans/heatmapy/`, {
    method: "GET",
    credentials: "omit",
    signal,
  });

  if (!res.ok) {
    throw new Error("Došlo k chybě při načítání dat ze serveru.");
  }
  return res.json();
};

const PriceList = ({ notLoggedClick, onLoadError, onChoosePlan }) => {
  const { t } = useTranslation();
  const { isLogged } = useContext(AppContext);
  const { lng } = useParams();
  const navigate = useNavigate();

  // Lokální loading stav pro payment
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [lowestPlan, setLowestPlan] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const priceListText = t("heatmaps.priceList", { returnObjects: true });

  const scrollContainerRef = useRef(null);
  const professionalPlanRef = useRef(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["servicePlans", isLogged],
    queryFn: fetchServicePlans,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    keepPreviousData: true,
    retry: 1,
  });

  // Extrahuj results pole z response
  const plans = data?.results || [];

  // Vrátí efektivní cenu: price již obsahuje všechny slevy z backendu
  const getEffectivePrice = (p) => {
    const price = p?.price;
    const n = Number(price);
    return Number.isFinite(n) ? n : 0;
  };

  // Když selže načítání ceníku: zavři kredit modal (pokud běží) a ukaž SimpleAlert přes callback z modalu
  useEffect(() => {
    if (isError) {
      console.error("Chyba při načítání ceníku:", error?.message);
      if (typeof onLoadError === "function") {
        onLoadError();
      } else {
        // Fallback původního chování (navigace na 404), pokud callback není k dispozici
        navigate(`/${lng}/404/`);
      }
    }
  }, [isError, error, onLoadError, navigate, lng]);

  // Scroll na zvýrazněný plán po načtení
  useEffect(() => {
    if (scrollContainerRef.current && professionalPlanRef.current) {
      const container = scrollContainerRef.current;
      const targetPlan = professionalPlanRef.current;
      const containerWidth = container.offsetWidth;
      const targetPlanWidth = targetPlan.offsetWidth;
      const targetPlanOffsetLeft = targetPlan.offsetLeft;
      const scrollPosition =
        targetPlanOffsetLeft - containerWidth / 2 + targetPlanWidth / 2;
      container.scrollLeft = scrollPosition;
    }
  }, [plans]);
  // Najdi nejnižší plán (podle názvu "Základní")
  useEffect(() => {
    if (plans.length > 0) {
      setLowestPlan(plans.find((plan) => plan.name === "Základní") || null);
    } else {
      setLowestPlan(null);
    }
  }, [plans]);

  // Zobrazit globální načítání během stahování plánů
  if (isLoading) {
    return <ModalLoading message="Načítám platební plány..." />;
  }

  const updateParams = (key, value) => {
    const params = new URLSearchParams(searchParams);

    if (value === null || value === "") {
      params.delete(key);
    } else {
      params.set(key, value);
    }

    setSearchParams(params);
  };

  const paymentCreate = async (planSlug) => {
    try {
      const response = await axiosInstance.post(`payments/create/`, {
        planSlug,
      });
      return response.data;
    } catch (err) {
      updateParams("p_status", "pending");
      console.log("An error occurred: ", err);
      throw err;
    }
  };

  const choosePlan = async (plan) => {
    const planSlug = typeof plan === "string" ? plan : plan?.slug;
    if (!isLogged) {
      notLoggedClick?.();
      return;
    }
    // Pokud máme custom handler (např. pro fakturační údaje v modalu), použij ho
    if (typeof onChoosePlan === "function") {
      try {
        onChoosePlan(plan);
      } catch (err) {
        console.error("Chyba při zpracování výběru plánu:", err);
      }
      return;
    }
    setIsProcessingPayment(true);
    try {
      const data = await paymentCreate(planSlug);

      if (data && data.redirectUrl) {
        window.location.href = data.redirectUrl;
      } else {
        console.error("Chyba: Odpověď z API neobsahuje redirectUrl.");
        setIsProcessingPayment(false);
      }
    } catch (err) {
      console.error("Nepodařilo se vytvořit platbu:", err);
      setIsProcessingPayment(false);
    }
  };

  return (
    <Pack dataComponent={"price-list"}>
      <h2 className="title">{priceListText.heading}</h2>

      <div className={styles.priceList} ref={scrollContainerRef}>
        {/* Dynamické plány z API */}
        {!isLoading &&
          plans.length > 0 &&
          plans.map((plan, idx) => {
            const isHighlighted = plan.name === "Profesionál"; // zvýrazněný plán
            const effectivePrice = getEffectivePrice(plan);
            // Pokud je sleva, zobraz 2 desetinná místa, jinak 0
            const useDecimals = Boolean(plan?.has_discount);
            const hoverProps =
              idx === 2 ? {} : { hoverBg: "white", hoverColor: "#000" };
            return (
              <div
                key={plan.id}
                ref={isHighlighted ? professionalPlanRef : undefined}
                className={
                  isHighlighted
                    ? `${styles.planBox} ${styles.invert}`
                    : styles.planBox
                }
              >
                <h4>{plan.name}</h4>

                {/* Když je sleva, zobraz nejdřív kredity */}
                {plan?.has_discount && plan?.original_price && (
                  <p className={styles.credits}>
                    {`${plan.credits_granted} ${priceListText.credits}`}
                  </p>
                )}

                {/* Zobrazení ceny */}
                <div style={{ margin: "12px 0" }}>
                  {plan?.has_discount && plan?.original_price ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        justifyContent: "flex-start",
                        flexWrap: "nowrap",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "2em",
                          fontWeight: "bold",
                          color: isHighlighted ? "#000" : "inherit",
                        }}
                      >
                        {formatCurrency(
                          effectivePrice,
                          plan.currency,
                          useDecimals ? 2 : 0,
                          useDecimals ? 2 : 0
                        )}
                      </span>
                      <span
                        style={{
                          textDecoration: "line-through",
                          color: isHighlighted ? "#CC4544" : "#FFC5C4",
                          fontSize: "1.2em",
                          fontWeight: "500",
                        }}
                      >
                        {formatCurrency(
                          plan.original_price,
                          plan.currency,
                          0,
                          0
                        )}
                      </span>
                    </div>
                  ) : (
                    <div
                      style={{
                        fontSize: "2em",
                        fontWeight: "bold",
                        textAlign: "left",
                        color: isHighlighted ? "#000" : "inherit",
                      }}
                    >
                      {formatCurrency(effectivePrice, plan.currency, 0, 0)}
                    </div>
                  )}
                </div>

                {/* Když není sleva, zobraz kredity tady */}
                {(!plan?.has_discount || !plan?.original_price) && (
                  <p className={styles.credits}>
                    {`${plan.credits_granted} ${priceListText.credits}`}
                  </p>
                )}

                {/* Název slevy jako řádek s pozadím */}
                {plan?.has_discount && plan?.discount_name && (
                  <div
                    style={{
                      backgroundColor: isHighlighted ? "#000" : "#22c55e",
                      color: isHighlighted ? "#22c55e" : "#000",
                      padding: "8px 12px",
                      borderRadius: "8px",
                      fontSize: "0.9em",
                      fontWeight: "bold",
                      margin: "12px 0",
                      textAlign: "center",
                    }}
                  >
                    {(() => {
                      const t = plan.discount_name;

                      const combined = Math.round(
                        (1 -
                          (t.match(/\d+%/g) || [])
                            .map((d) => parseInt(d) / 100)
                            .reduce((acc, v) => acc * (1 - v), 1)) *
                          100
                      );

                      const onlyNames = t
                        .replace(/\d+%/g, "") // odstraní "50%"
                        .replace(/\s+\+/g, " +") // vyčistí mezery před +
                        .trim();

                      return `${onlyNames}  ${combined} %`;
                    })()}
                  </div>
                )}

                <div>
                  <div className={styles.line}></div>
                  {plan?.additional_info ? (
                    <>
                      <p className={styles.perk}>{plan.additional_info}</p>
                      <div className={styles.line}></div>
                    </>
                  ) : null}
                  <p className={styles.perk}>
                    {"Až " +
                      plan.credits_granted +
                      " otestovaných" +
                      " bannerů"}
                  </p>
                  <div className={styles.line}></div>

                  {lowestPlan?.credits_granted ? (
                    <>
                      <p className={styles.perk}>
                        {(() => {
                          const lowestEffective = getEffectivePrice(lowestPlan);
                          const baseUnit =
                            lowestEffective / lowestPlan.credits_granted;
                          const thisUnit =
                            effectivePrice / plan.credits_granted;
                          if (!Number.isFinite(baseUnit) || baseUnit <= 0) {
                            return "—";
                          }
                          const discount = (
                            ((baseUnit - thisUnit) / baseUnit) *
                            100
                          ).toFixed(0);
                          return Number(discount) === 0
                            ? "—"
                            : `${discount} % sleva`;
                        })()}
                      </p>
                      <div className={styles.line}></div>
                    </>
                  ) : null}

                  <p className={styles.perk}>
                    {`${formatCurrency(
                      effectivePrice / plan.credits_granted,
                      plan.currency,
                      2,
                      2
                    )} / banner`}
                  </p>
                  <div className={styles.line}></div>

                  <ButtonBtn
                    onClick={(e) => {
                      e.target.blur?.();
                      choosePlan(plan);
                    }}
                    style={{ marginInline: "auto", marginTop: "24px" }}
                    {...hoverProps}
                  >
                    {isLogged ? priceListText.btnBuy : priceListText.btnTest}
                  </ButtonBtn>
                </div>
              </div>
            );
          })}
      </div>

      {isProcessingPayment && <ModalLoading message="Zpracovávám platbu..." />}
    </Pack>
  );
};

export default PriceList;
